import classNames from 'classnames'
import React, { useState } from 'react'
import { INFLUENCER_SOCIALS, isValidWebUrl } from './content'
import { styles } from './styles'

interface FlowProps {
  submitting: boolean
  error: string
  onBack: () => void
  onSubmit: (
    answers: Record<string, any>,
    consents: Record<string, boolean>
  ) => void
}

type Presence = 'web' | 'social'

const PRESENCE_OPTIONS: { id: Presence; label: string }[] = [
  { id: 'web', label: 'Website / blog' },
  { id: 'social', label: 'Social handles' },
]

const InfluencerFlow = ({ submitting, error, onBack, onSubmit }: FlowProps) => {
  const classes = styles()
  const [presence, setPresence] = useState<Presence>('web')
  const [website, setWebsite] = useState('')
  const [socials, setSocials] = useState<Record<string, string>>({})
  const [localError, setLocalError] = useState('')

  // Social handles must not contain spaces, so strip any whitespace as typed.
  const setSocial = (id: string, value: string) =>
    setSocials(prev => ({ ...prev, [id]: value.replace(/\s+/g, '') }))

  const handleGenerate = () => {
    if (presence === 'web' && !isValidWebUrl(website)) {
      setLocalError('Please enter a valid website or blog URL.')
      return
    }
    if (
      presence === 'social' &&
      !INFLUENCER_SOCIALS.some(s => (socials[s.id] || '').trim())
    ) {
      setLocalError('Please add at least one handle.')
      return
    }
    setLocalError('')
    const cleanedSocials: Record<string, string> = {}
    INFLUENCER_SOCIALS.forEach(s => {
      const v = (socials[s.id] || '').trim()
      if (v) cleanedSocials[s.id] = v
    })
    onSubmit(
      {
        presenceType: presence,
        website: presence === 'web' ? website.trim() : '',
        socials: presence === 'social' ? cleanedSocials : {},
      },
      {}
    )
  }

  return (
    <div>
      <p className={classes.intro}>
        Tell us where you share. Add a website, blog, or at least one social
        handle.
      </p>
      <div className={classes.tabRow}>
        {PRESENCE_OPTIONS.map(opt => (
          <button
            key={opt.id}
            type="button"
            className={classNames(classes.tab, {
              [classes.tabActive]: presence === opt.id,
            })}
            onClick={() => {
              setPresence(opt.id)
              setLocalError('')
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {presence === 'web' && (
        <div>
          <label className={classes.label}>Website / blog / Substack URL</label>
          <input
            className={classes.input}
            type="url"
            placeholder="https://yoursite.com"
            value={website}
            onChange={e => setWebsite(e.target.value)}
          />
        </div>
      )}

      {presence === 'social' && (
        <div>
          {INFLUENCER_SOCIALS.map(s => (
            <div key={s.id} className={classes.socialRow}>
              <span style={{ fontSize: 12, fontWeight: 500, minWidth: 78 }}>
                {s.label}
              </span>
              <span style={{ fontSize: 12, color: '#888' }}>{s.prefix}</span>
              <input
                className={classes.input}
                style={{
                  height: 32,
                  border: 'none',
                  background: 'transparent',
                }}
                placeholder="handle"
                value={socials[s.id] || ''}
                onChange={e => setSocial(s.id, e.target.value)}
              />
            </div>
          ))}
        </div>
      )}

      {(localError || error) && (
        <p className={classes.error}>{localError || error}</p>
      )}
      <div className={classes.btnRow}>
        <button type="button" className={classes.backBtn} onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className={classNames(classes.primaryBtn, classes.rowPrimaryBtn)}
          disabled={submitting}
          onClick={handleGenerate}
        >
          {submitting ? 'Generating' : 'Generate share link'}
        </button>
      </div>
    </div>
  )
}

export default InfluencerFlow
